import { resolveLandingMedia } from "./landingMedia";
export const CUSTOM_VISUAL_DEFAULTS = Object.freeze({
  background: Object.freeze({ color: "#FFFFFF", imageUrl: "", imagePosition: "center", imageSize: "cover", overlayColor: "#000000", overlayOpacity: 0 }),
  layout: Object.freeze({ spacingScale: 0.88, heightMode: "auto", heightMobile: 400, heightDesktop: 600 }),
  textShadow: Object.freeze({ color: "#000000", opacity: 0 }),
  eyebrow: Object.freeze({ color: "#F35A3A", fontWeight: 900, letterSpacing: 0.14 }),
  title: Object.freeze({ color: "#19242F", fontScale: 0.9, fontWeight: 900 }),
  description: Object.freeze({ color: "#5B5D73", lineHeight: 1.75 }),
  image: Object.freeze({ backgroundColor: "transparent", borderColor: "transparent", borderWidth: 0, borderRadius: 32, shadowColor: "#000000", shadowOpacity: 0, shadowX: 0, shadowY: 0, shadowBlur: 0, shadowSpread: 0, heightMobile: 260, heightDesktop: 343, objectFit: "cover" }),
  cta: Object.freeze({ backgroundColor: "#F35A3A", textColor: "#FFFFFF", hoverBackgroundColor: "#D94D32", hoverTextColor: "#FFFFFF", borderColor: "transparent", borderWidth: 0, borderRadius: 999, shadowColor: "#000000", shadowOpacity: 0, shadowX: 0, shadowY: 0, shadowBlur: 0, shadowSpread: 0, focusColor: "rgba(243,90,58,.35)", fontWeight: 900 }),
  youtube: Object.freeze({ backgroundColor: "#000000", borderColor: "transparent", borderWidth: 0, borderRadius: 32, shadowColor: "#000000", shadowOpacity: 0, shadowX: 0, shadowY: 0, shadowBlur: 0, shadowSpread: 0 }),
  calendly: Object.freeze({ backgroundColor: "#FFFFFF", borderColor: "transparent", borderWidth: 0, borderRadius: 32, shadowColor: "#000000", shadowOpacity: 0, shadowX: 0, shadowY: 0, shadowBlur: 0, shadowSpread: 0, heightMobile: 680, heightDesktop: 760 }),
});
const objectValue = (value) => value && typeof value === "object" && !Array.isArray(value) ? value : {};
export function normalizeCustomSegmentStyle(style = {}) {
  const source = objectValue(style); const custom = objectValue(source.custom);
  return { background: { ...CUSTOM_VISUAL_DEFAULTS.background, ...objectValue(source.background) }, custom: {
    layout: { ...CUSTOM_VISUAL_DEFAULTS.layout, ...objectValue(custom.layout) }, textShadow: { ...CUSTOM_VISUAL_DEFAULTS.textShadow, ...objectValue(custom.textShadow) }, eyebrow: { ...CUSTOM_VISUAL_DEFAULTS.eyebrow, ...objectValue(custom.eyebrow) }, title: { ...CUSTOM_VISUAL_DEFAULTS.title, ...objectValue(custom.title) }, description: { ...CUSTOM_VISUAL_DEFAULTS.description, ...objectValue(custom.description) }, image: { ...CUSTOM_VISUAL_DEFAULTS.image, ...objectValue(custom.image) }, cta: { ...CUSTOM_VISUAL_DEFAULTS.cta, ...objectValue(custom.cta) }, youtube: { ...CUSTOM_VISUAL_DEFAULTS.youtube, ...objectValue(custom.youtube) }, calendly: { ...CUSTOM_VISUAL_DEFAULTS.calendly, ...objectValue(custom.calendly) }, } };
}
// "Fija" reserva un alto estable aun sin imagen; el contenido puede crecer sin recortarse.
export function customSectionHeightSx(layout = {}, previewDevice = null) {
  if (layout?.heightMode !== "fixed") return {};
  const safeHeight = (value, fallback, min, max) => {
    const number = Number(value);
    return Math.min(max, Math.max(min, Number.isFinite(number) ? number : fallback));
  };
  const mobile = safeHeight(layout.heightMobile, 400, 180, 1200);
  const desktop = safeHeight(layout.heightDesktop, 600, 220, 1600);
  return {
    minHeight: previewDevice ? (previewDevice === "mobile" ? mobile : desktop) : { xs: mobile, md: desktop },
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  };
}

function alphaColor(color, opacity) { const raw=String(color||"").trim(); if(!raw||Number(opacity)<=0)return "transparent"; if(Number(opacity)>=1)return raw; const hex=raw.match(/^#([0-9a-f]{6})$/i)?.[1]; if(!hex)return raw; return `rgba(${parseInt(hex.slice(0,2),16)}, ${parseInt(hex.slice(2,4),16)}, ${parseInt(hex.slice(4,6),16)}, ${Number(opacity)})`; }
export function customShadow(style={}) { const opacity=Number(style.shadowOpacity)||0; if(opacity<=0)return "none"; return `${Number(style.shadowX)||0}px ${Number(style.shadowY)||0}px ${Math.max(0,Number(style.shadowBlur)||0)}px ${Number(style.shadowSpread)||0}px ${alphaColor(style.shadowColor||"#000000",opacity)}`; }

// Una capa de color no debe tapar la imagen: es el primer background-image
// (se pinta encima de la imagen), y el fondo base queda por debajo.
export function customOverlayColor(color, opacity) {
  const strength = Math.min(1, Math.max(0, Number(opacity) || 0));
  const raw = String(color || "").trim();
  if (!raw || raw.toLowerCase() === "transparent" || strength <= 0) return "transparent";
  if (strength >= 1) return raw;

  const hex = /^#([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i.exec(raw)?.[1];
  if (hex) {
    const expanded = hex.length <= 4 ? hex.split("").map((digit) => digit + digit).join("") : hex;
    const alpha = expanded.length === 8 ? parseInt(expanded.slice(6, 8), 16) / 255 : 1;
    const channels = [0, 2, 4].map((index) => parseInt(expanded.slice(index, index + 2), 16));
    return `rgba(${channels.join(", ")}, ${Number((strength * alpha).toFixed(4))})`;
  }
  const rgb = /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})(?:\s*,\s*(0?\.\d+|1(?:\.0+)?|0))?\s*\)$/i.exec(raw);
  if (rgb) {
    const alpha = rgb[4] === undefined ? 1 : Math.min(1, Math.max(0, Number(rgb[4])));
    return `rgba(${rgb[1]}, ${rgb[2]}, ${rgb[3]}, ${Number((strength * alpha).toFixed(4))})`;
  }
  // CSS colors with HSL/named values retain their base alpha via color-mix.
  return `color-mix(in srgb, ${raw} ${Number((strength * 100).toFixed(2))}%, transparent)`;
}

export function customBackgroundSx(style = {}, legacyVisualStyle = {}) {
  const background = normalizeCustomSegmentStyle(style).background;
  const imageUrl = resolveLandingMedia(background.imageUrl || legacyVisualStyle?.backgroundImageUrl || "");
  const sx = { backgroundColor: background.color };
  if (!imageUrl) return sx;
  // JSON.stringify conserva entre comillas URLs con espacios, query strings o paréntesis.
  const image = `url(${JSON.stringify(String(imageUrl))})`;
  const overlay = customOverlayColor(background.overlayColor, background.overlayOpacity);
  const hasOverlay = overlay !== "transparent";
  sx.backgroundImage = hasOverlay ? `linear-gradient(${overlay}, ${overlay}), ${image}` : image;
  sx.backgroundPosition = hasOverlay ? `center, ${background.imagePosition}` : background.imagePosition;
  sx.backgroundSize = hasOverlay ? `cover, ${background.imageSize}` : background.imageSize;
  sx.backgroundRepeat = hasOverlay ? "no-repeat, no-repeat" : "no-repeat";
  return sx;
}


// Sombra de letras, no box-shadow ni filtro sobre el contenedor.
export function customTextShadow(style={}) {
 const opacity=Math.min(1,Math.max(0,Number(style.opacity)||0));
 const color=customOverlayColor(style.color||"#000000",opacity);
 return color==="transparent" ? "none" : `0 2px 8px ${color}, 0 0 16px ${color}`;
}
